import React, { forwardRef } from 'react';
import MuiTypography, { TypographyProps as MuiTypographyProps } from '@mui/material/Typography';
import { BpDesignSystem } from '../../types';
import { mergeClassNames } from '../../utils';
import { getTokenColor, TokenColor } from '../../setup/theme/impl/colors';

type PartialTypographyProps = MuiTypographyProps<'div', {}>;

export { type TokenColor };
export type TypographyVariants = 'display' | 'heading' | 'title' | 'body' | 'label';

/**
 * @deprecated use TypographyVariants instead. These legacy variants will be removed in a future release. See the migration guide for more details:
 */
export type TypographyLegacyVariants =
  | 'hint'
  | 'body1'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'small'
  | 'label2'
  | 'label1'
  | 'body2'
  | 'body1Bold'
  | 'labelSmall';

export type TypographyProps = BpDesignSystem.PropsWithChildren<
  Omit<PartialTypographyProps, 'variant' | 'color'> & {
    variant?: TypographyLegacyVariants | TypographyVariants;
    color?: 'initial' | TokenColor | `#${string}` | `var(${string})`;
    small?: boolean;
    large?: boolean;
    underline?: boolean;
    italic?: boolean;
    dataTest?: string;
    inline?: boolean;
    thin?: boolean;
  }
>;

export const Typography = forwardRef<any, TypographyProps>(
  (
    {
      dataTest,
      color,
      children,
      variant = 'body',
      className,
      small,
      large,
      underline,
      italic,
      inline,
      sx,
      thin,
      ...rest
    },
    ref
  ) => {
    const targetVariantAndSize = mapVariants(variant);
    const targetVariant = targetVariantAndSize.variant;
    const targetSmall = small ?? targetVariantAndSize.small;
    const targetLarge = large ?? targetVariantAndSize.large;
    const targetClassName = mergeClassNames(className, {
      [`BpTypography-variant-${targetVariant}`]: true,
      'BpTypography-size-large': targetLarge,
      'BpTypography-size-small': targetSmall,
      'BpTypography-size-medium': !targetSmall && !targetLarge,
      'BpTypography-style-italic': italic,
      'BpTypography-decoration-underline': underline,
      'BpTypography-display-inline': inline,
      'BpTypography-thin': thin
    });

    return (
      <MuiTypography
        ref={ref}
        {...mapMuiProps({
          variant: targetVariant,
          small: targetSmall,
          large: targetLarge
        })}
        sx={{
          color: getTokenColor(color),
          ...sx
        }}
        data-test={dataTest}
        className={targetClassName}
        {...rest}
      >
        {children}
      </MuiTypography>
    );
  }
);

function mapVariants(variant: TypographyLegacyVariants | TypographyVariants): {
  variant: TypographyVariants;
  large?: boolean;
  small?: boolean;
} {
  switch (variant) {
    case 'display':
    case 'heading':
    case 'title':
    case 'body':
    case 'label':
      return { variant };
    case 'h1':
      return { variant: 'heading' };
    case 'h2':
      return { variant: 'heading', small: true };
    case 'h3':
      return { variant: 'title', large: true };
    case 'h4':
      return { variant: 'title' };
    case 'h5':
      return { variant: 'title', small: true };
    case 'body1':
      return { variant: 'body' };
    case 'body2':
      return { variant: 'body', small: true };
    case 'body1Bold':
      return { variant: 'body', large: true };
    case 'label1':
      return { variant: 'label', large: true };
    case 'label2':
      return { variant: 'label' };
    case 'labelSmall':
      return { variant: 'label', small: true };
    case 'hint':
      return { variant: 'body', small: true };
    case 'small':
      return { variant: 'body', small: true };
    default:
      return { variant: 'body' };
  }
}

interface ToMuiPropsProps {
  variant: TypographyVariants;
  small?: boolean;
  large?: boolean;
}

function mapMuiProps({ large, small, variant }: ToMuiPropsProps): Partial<MuiTypographyProps> {
  switch (variant) {
    case 'display':
      return {
        variant: 'h1',
        component: 'h1'
      };
    case 'heading':
      if (small)
        return {
          variant: 'h3',
          component: 'h3'
        };
      return {
        variant: 'h2',
        component: 'h2'
      };
    case 'title':
      if (large)
        return {
          variant: 'h4',
          component: 'h4'
        };
      if (small)
        return {
          variant: 'h6',
          component: 'h6'
        };
      return {
        variant: 'h5',
        component: 'h5'
      };
    case 'body':
      return {
        variant: 'body1',
        component: 'div'
      };
    case 'label':
      return {
        variant: 'body1',
        component: 'div'
      };
  }
}
